import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Button, CircularProgress, IconButton, Snackbar, Alert, Tooltip } from "@mui/material";
import { Check, ContentCopy, Download, IosShare } from "@mui/icons-material";
import { answerDeskService, FaqImage } from "../../../services/answerDeskService";
import { ImageLightbox, LightboxImage } from "../../../components/media/ImageLightbox";
import { ImageThumbnailStrip } from "../../../components/media/ImageThumbnailStrip";
import {
    CopyOutcome,
    copyImageWithFallback,
    downloadBlob,
    fileStem,
    imageCopyMode,
} from "../../../components/media/imageActions";
import { imageNumberShortcut } from "../answerDeskUtils";

interface AnswerImagesProps {
    images: FaqImage[];
    /** Used for alt text and download file names. */
    question: string;
    /** Thumbnail edge in px. */
    size?: number;
    /** Name each image's keyboard shortcut in its Copy tooltip (the
     * highlighted answer on the Answer Desk / quick answers). */
    shortcutHints?: boolean;
}

/** Lets the Answer Desk's keyboard shortcuts copy an image. Call it straight
 * from the key handler — Safari only allows the clipboard write there. */
export interface AnswerImagesHandle {
    copy: (index: number) => void;
}

type Busy = { id: string; action: "copy" | "download" } | null;

// Buttons sit on the viewer's dark background.
const LIGHTBOX_BUTTON_SX = { minHeight: 44, minWidth: 120 };
const LIGHTBOX_PRIMARY_SX = { bgcolor: "common.white", color: "grey.900", "&:hover": { bgcolor: "grey.200" } };
const LIGHTBOX_OUTLINE_SX = { color: "common.white", borderColor: "rgba(255,255,255,0.5)", "&:hover": { borderColor: "common.white" } };

// Comfortable finger targets on phones, compact on desktop.
const THUMB_ACTION_SX = { width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } };

const OUTCOME_MESSAGE: Record<CopyOutcome, string | null> = {
    copied: "Image copied — paste it in the chat",
    shared: null,
    downloaded: "Image downloaded",
    cancelled: null,
};

/**
 * An answer's pictures: thumbnails that open full size, each with Copy and
 * Download (also in the full-size viewer). Copy uses the clipboard on
 * desktop and the share sheet on phones (see imageCopyMode).
 */
export const AnswerImages = forwardRef<AnswerImagesHandle, AnswerImagesProps>(function AnswerImages(
    { images, question, size = 84, shortcutHints = false },
    ref,
) {
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const [busy, setBusy] = useState<Busy>(null);
    const [doneId, setDoneId] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; severity: "success" | "error" } | null>(null);
    const doneTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copyMode = useMemo(() => imageCopyMode(), []);

    const stem = fileStem(question, "answer-image");
    const fileName = (index: number) => `${stem}-${index + 1}.jpg`;
    const alt = (index: number) =>
        images.length > 1 ? `${question} — image ${index + 1}` : question;

    const lightboxImages: LightboxImage[] = images.map((img, i) => ({
        id: img.faq_image_id,
        src: img.large_url,
        alt: alt(i),
    }));
    const thumbs = images.map((img, i) => ({ id: img.faq_image_id, src: img.thumbnail_url, alt: alt(i) }));

    const flashDone = (id: string) => {
        setDoneId(id);
        if (doneTimer.current) clearTimeout(doneTimer.current);
        doneTimer.current = setTimeout(() => setDoneId(null), 2000);
    };

    const copy = async (index: number) => {
        const image = images[index];
        setBusy({ id: image.faq_image_id, action: "copy" });
        try {
            const outcome = await copyImageWithFallback(
                () => answerDeskService.getImageBlob(image.faq_image_id, "large"),
                fileName(index),
                question,
            );
            if (outcome !== "cancelled") flashDone(image.faq_image_id);
            const message = OUTCOME_MESSAGE[outcome];
            if (message) setToast({ message, severity: "success" });
        } catch {
            setToast({ message: "Couldn't copy the image. Try Download instead.", severity: "error" });
        } finally {
            setBusy(null);
        }
    };

    useImperativeHandle(ref, () => ({
        copy: (index: number) => {
            if (images[index] && !busy) void copy(index);
        },
    }));

    const download = async (index: number) => {
        const image = images[index];
        setBusy({ id: image.faq_image_id, action: "download" });
        try {
            downloadBlob(await answerDeskService.getImageBlob(image.faq_image_id, "original"), fileName(index));
        } catch {
            setToast({ message: "Couldn't download the image. Check your connection and try again.", severity: "error" });
        } finally {
            setBusy(null);
        }
    };

    if (images.length === 0) return null;

    const copyLabel = copyMode === "share" ? "Share" : copyMode === "download" ? "Save" : "Copy";
    const CopyIcon = copyMode === "share" ? IosShare : ContentCopy;
    const isBusy = (id: string, action: "copy" | "download") => busy?.id === id && busy.action === action;

    return (
        <>
            <ImageThumbnailStrip
                images={thumbs}
                size={size}
                onOpen={setOpenIndex}
                renderActions={(thumb, index) => (
                    <>
                        <Tooltip
                            title={
                                shortcutHints && copyMode === "copy" && index < 9
                                    ? `${copyLabel} image (${imageNumberShortcut(index + 1)})`
                                    : `${copyLabel} image`
                            }
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => copy(index)}
                                    disabled={!!busy}
                                    aria-label={`${copyLabel} image ${index + 1}`}
                                    color={doneId === thumb.id ? "success" : "default"}
                                    sx={THUMB_ACTION_SX}
                                >
                                    {isBusy(thumb.id, "copy") ? (
                                        <CircularProgress size={16} />
                                    ) : doneId === thumb.id ? (
                                        <Check fontSize="small" />
                                    ) : (
                                        <CopyIcon fontSize="small" />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title="Download full size">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => download(index)}
                                    disabled={!!busy}
                                    aria-label={`Download image ${index + 1}`}
                                    sx={THUMB_ACTION_SX}
                                >
                                    {isBusy(thumb.id, "download") ? <CircularProgress size={16} /> : <Download fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </>
                )}
            />

            <ImageLightbox
                images={lightboxImages}
                index={openIndex}
                onIndexChange={setOpenIndex}
                onClose={() => setOpenIndex(null)}
                renderActions={(image, index) => (
                    <>
                        <Button
                            variant="contained"
                            disableElevation
                            color={doneId === image.id ? "success" : "primary"}
                            startIcon={
                                isBusy(image.id, "copy") ? (
                                    <CircularProgress size={16} color="inherit" />
                                ) : doneId === image.id ? (
                                    <Check />
                                ) : (
                                    <CopyIcon />
                                )
                            }
                            onClick={() => copy(index)}
                            disabled={!!busy}
                            sx={doneId === image.id ? LIGHTBOX_BUTTON_SX : { ...LIGHTBOX_BUTTON_SX, ...LIGHTBOX_PRIMARY_SX }}
                        >
                            {doneId === image.id ? "Done" : `${copyLabel} image`}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={isBusy(image.id, "download") ? <CircularProgress size={16} color="inherit" /> : <Download />}
                            onClick={() => download(index)}
                            disabled={!!busy}
                            sx={{ ...LIGHTBOX_BUTTON_SX, ...LIGHTBOX_OUTLINE_SX }}
                        >
                            Download
                        </Button>
                    </>
                )}
            />

            {/* Top, not bottom: at the bottom it would sit over the
                full-size viewer's Copy/Download buttons on phones. */}
            <Snackbar
                open={!!toast}
                autoHideDuration={3000}
                onClose={() => setToast(null)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setToast(null)} severity={toast?.severity ?? "success"} variant="filled" sx={{ width: "100%" }}>
                    {toast?.message}
                </Alert>
            </Snackbar>
        </>
    );
});
